// Holding one option and turning the hold into a cart line — the shape every
// per-option add path in ServicePanel already used (multi-select loop,
// single-select list path, per-card add), extracted with zero behaviour change
// so a second panel adds the SAME line instead of a copy.
//
// Money-path rule this module keeps: a cart line is only ever built FROM a
// successful hold's own response (booking_id, amount_cents) — checkout charges
// every hold under the cart_token, so a line without a hold (or vice versa)
// breaks the money path.

import type { WidgetService, WidgetUnit } from "../types";
import { heldBookingWindow } from "@vertu/shared";
import type { CartItem } from "./cart";
import { partyMinFor } from "./option-led";
import {
  type BookingWindow,
  type HoldResponse,
  cancelCartBooking,
  createBookingHold,
  getCartToken,
  stampVisitPartySize,
} from "./booking";

export type HeldBooking = Extract<HoldResponse, { ok: true }>;

/**
 * One hold for one option (or none, for a unit-less list hold) on `window`
 * exactly as given — the caller decides the hold's party and length. The
 * hold ONLY: it does not stamp the visit's guest count, so a caller placing
 * several holds for one visit can stamp once, after all of them.
 */
export function holdUnit(input: {
  serviceId: string;
  unitId: string | null;
  window: BookingWindow;
}): Promise<HoldResponse> {
  return createBookingHold({
    serviceId: input.serviceId,
    floorPlanId: null,
    elementId: null,
    serviceUnitId: input.unitId,
    seatingAreaId: null,
    window: input.window,
    guest: { name: null, email: null, phone: null },
    cartToken: getCartToken(),
  });
}

/**
 * holdUnit, then stamp the visit's guest count after the call — exactly what
 * every existing ServicePanel call site did (the stamp runs whether or not
 * the hold succeeded).
 */
export async function holdServiceUnit(input: {
  serviceId: string;
  unitId: string | null;
  window: BookingWindow;
  /** Whole-visit headcount for set_cart_party_size (not the hold's party). */
  visitPartySize: number;
}): Promise<HoldResponse> {
  const hold = await holdUnit(input);
  stampVisitPartySize(input.visitPartySize);
  return hold;
}

/** The cart line for a successful option hold. Pure. */
export function buildUnitCartLine(input: {
  service: WidgetService;
  unit: WidgetUnit | null;
  hold: HeldBooking;
  /** The window the hold was taken for (its party_size is the hold's party). */
  window: BookingWindow;
  quantity: number;
  unitPrice: number | null;
  visitGuests: number;
  pricingBasis: "per_person" | "per_unit";
  brandName: string | null;
}): Omit<CartItem, "id"> {
  const { service, unit, hold } = input;
  const w = heldBookingWindow(input.window, hold);
  return {
    serviceId: service.id,
    serviceName: service.name,
    serviceType: service.service_type,
    unitId: unit?.id ?? null,
    unitName: unit?.name ?? null,
    description: describeUnit(service, unit, input.quantity),
    quantity: input.quantity,
    unitPrice: typeof hold.unit_price_cents === "number" ? hold.unit_price_cents / 100 : input.unitPrice,
    currency: unit?.currency ?? "ZAR",
    brandId: service.brand_id,
    brandName: input.brandName,
    metadata: {
      booking_id: hold.booking_id,
      booking_reference: hold.reference ?? null,
      starts_at: w.starts_at,
      ends_at: w.ends_at,
      party_size: w.party_size,
      visit_guests: input.visitGuests,
      unit_capacity: unit?.capacity ?? null,
      pricing_basis: input.pricingBasis,
      amount_cents: hold.amount_cents,
      requires_full_payment: hold.requires_full_payment,
      // The option's fewest guests per booking: the cart's guests stepper
      // stops there (the hold would refuse fewer). Only when there is one.
      ...(input.pricingBasis === "per_person" && partyMinFor(service, unit) >= 2
        ? { party_size_min: partyMinFor(service, unit) }
        : {}),
    },
  };
}

export function describeUnit(
  service: WidgetService,
  unit: WidgetUnit | null,
  _qty: number,
): string {
  // Title only — no "× qty" suffix. CartPanel renders
  // <span className="vt-cart-item-qty"> · × {item.quantity}</span>
  // separately, so including it here produced doubled "× 2 · × 2"
  // in the cart row title.
  const parts = [service.name];
  if (unit) parts.push(unit.name);
  return parts.join(" — ");
}

// This option's own cart lines. Combo lines are excluded — they travel as a
// package and are removed whole from the cart drawer, so a card Remove must
// never silently unwind a combo.
export function unitCartLines(
  items: CartItem[],
  serviceId: string,
  unitId: string,
): CartItem[] {
  return items.filter(
    (i) =>
      i.serviceId === serviceId &&
      i.unitId === unitId &&
      !i.metadata?.combo_id,
  );
}

// Per-unit cart total for the cards' "In booking × N ✓" badges.
export function unitCartCount(
  items: CartItem[],
  serviceId: string,
  unitId: string,
): number {
  return unitCartLines(items, serviceId, unitId).reduce(
    (n, i) => n + (i.quantity ?? 1),
    0,
  );
}

// Per-card remove: cancel every hold this option has in the cart, then drop
// the lines. Remove + re-Add is the safe primitive — cart.updateQty is
// local-only, so mutating an in-cart quantity would desync the charged
// holds.
export function removeUnitLines(
  items: CartItem[],
  serviceId: string,
  unitId: string,
  remove: (itemId: string) => void,
): void {
  const doomed = unitCartLines(items, serviceId, unitId);
  for (const item of doomed) {
    const bookingId = item.metadata?.booking_id;
    if (typeof bookingId === "string") {
      void cancelCartBooking(bookingId); // best-effort; expired holds no-op
    }
    remove(item.id);
  }
}

// Covers already reserved for this service by in-cart lines.
export function coversInCartFor(items: CartItem[], serviceId: string): number {
  return items
    .filter((i) => i.serviceId === serviceId)
    .reduce((sum, i) => sum + Number(i.metadata?.party_size ?? 0), 0);
}
